import React, { useEffect, useMemo, useRef, useState } from "react";
import TitleButtons from "../../../components/TitleButtons";
import TextField from "../../../components/TextField";
import Button from "../../../components/Button";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";

/** =================== Config/API =================== */
const API_POST =
  "https://api.eva-googlemarine.com/departamentos/recursos-humanos/registrar-colaborador";

const REQUEST_TIMEOUT_MS = 8000;

/** =================== Tipos =================== */
type ApiColaborador = {
  id: number | string;
  nome_completo: string;
  cpf: string;
  data_de_nascimento?: string | null;
  setor?: string | null;
  cargo?: string | null;
  salario?: number | string | null;
  email?: string | null;
  telefone?: string | null;
  contato_de_emergencia?: string | null;
  validade_do_contrato?: string | null;
  status_atual?: string | null;
  criado_em?: string | null;
  atualizado_em?: string | null;
};

type ApiResponse =
  | {
      ok: true;
      data?: ApiColaborador | { colaborador?: ApiColaborador };
      criado?: boolean;
      editado?: boolean;
      status_alterado?: boolean;
    }
  | {
      ok: false;
      erro?: string;
      erros?: string[];
      detalhe?: string;
      message?: string;
      auth?: any;
    };

/** =================== Helpers/Máscaras =================== */
const onlyDigits = (s: unknown) => String(s ?? "").replace(/\D+/g, "");

function maskCPF(s: string): string {
  const d = onlyDigits(s).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return d.replace(/^(\d{3})(\d+)/, "$1.$2");
  if (d.length <= 9) return d.replace(/^(\d{3})(\d{3})(\d+)/, "$1.$2.$3");
  return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2}).*/, "$1.$2.$3-$4");
}

function toISODate(input: string): string | null {
  const s = (input || "").trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00:00.000Z`;
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) {
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}T00:00:00.000Z`;
  }
  return null;
}

function fromISOToBR(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function isValidEmail(x: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x);
}

function salarioApiToView(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "";
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  if (!Number.isFinite(n)) return "";
  return n
    .toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/\s/g, "");
}

/** =================== Auth helpers (MESMO PADRÃO DO SUPRIMENTOS) =================== */
function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("access_token");
  } catch {
    return null;
  }
}

function getEvaAuthUid(): number | null {
  try {
    const raw =
      localStorage.getItem("eva_auth_uid") ||
      sessionStorage.getItem("eva_auth_uid") ||
      null;

    if (!raw) return null;
    const s = String(raw).trim();
    if (!/^\d+$/.test(s)) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function buildUidHeaders(): Record<string, string> {
  const uid = getEvaAuthUid();
  if (uid == null) return {};
  return {
    "x-user-id": String(uid),
    "x-eva-auth-uid": String(uid),
  };
}

/** =================== Banner (erro/aviso) =================== */
const NoticeBanner: React.FC<{
  notice: { type: "success" | "error" | "notify"; text: string };
  onClose?: () => void;
  sticky?: boolean;
}> = ({ notice, onClose, sticky = false }) => {
  let bg: string, color: string, border: string;
  switch (notice.type) {
    case "success":
      bg = "#e6ffed";
      color = "#0f5132";
      border = "#badbcc";
      break;
    case "error":
      bg = "#ffecec";
      color = "#842029";
      border = "#f5c2c7";
      break;
    default:
      bg = "#e7f1ff";
      color = "#084298";
      border = "#b6d4fe";
      break;
  }
  return (
    <motion.div
      role="alert"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      style={{
        margin: "12px 0",
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        border: `1px solid ${border}`,
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1 }}>{notice.text}</span>
        {onClose && (
          <button
            type="button"
            aria-label="Fechar aviso"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        )}
      </div>
    </motion.div>
  );
};

/** =================== Animações =================== */
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.06,
      delayChildren: 0.02,
    },
  },
};
const itemVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 320, damping: 24 },
  },
};

/** =================== Dropdown (mesmo estilo dos inputs) =================== */
type Option = { value: string; label: string };

const STATUS_OPTIONS: Option[] = [
  { value: "DISPONÍVEL", label: "DISPONÍVEL" },
  { value: "EM SERVIÇO", label: "EM SERVIÇO" },
  { value: "ATESTADO-MÉDICO", label: "ATESTADO-MÉDICO" },
  { value: "FÉRIAS", label: "FÉRIAS" },
  { value: "LICENÇA-MATERNIDADE", label: "LICENÇA-MATERNIDADE" },
  { value: "LICENÇA-PATERNIDADE", label: "LICENÇA-PATERNIDADE" },
  { value: "AFASTADO", label: "AFASTADO" },
  { value: "TREINAMENTO", label: "TREINAMENTO" },
  { value: "SUSPENSO", label: "SUSPENSO" },
  { value: "DESLIGADO", label: "DESLIGADO" },
];

const SelectField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
}> = ({ label, value, onChange, options, disabled, placeholder, style }) => {
  return (
    <div style={{ width: "100%", ...style }}>
      <div
        style={{
          fontSize: 12,
          marginBottom: 6,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {label}
      </div>

      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: "100%",
          height: 44,
          borderRadius: 8,
          padding: "0 12px",
          border: "1px solid rgba(0,0,0,0.15)",
          outline: "none",
          background: disabled ? "rgba(0,0,0,0.03)" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          fontSize: 14,
        }}
      >
        <option value="" disabled>
          {placeholder ?? "Selecione..."}
        </option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

/** =================== Página =================== */
const EditarColaborador: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation() as any;

  const colabFromState: ApiColaborador | null = location?.state?.colab ?? null;

  // Campos
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [cpfView, setCpfView] = useState("");
  const [dataNasc, setDataNasc] = useState("");
  const [setor, setSetor] = useState("");
  const [cargo, setCargo] = useState("");
  const [salario, setSalario] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [contatoEmerg, setContatoEmerg] = useState("");
  const [validadeContrato, setValidadeContrato] = useState("");
  const [statusAtual, setStatusAtual] = useState<string>("");

  // UI
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState<boolean>(!!id);
  const [notice, setNotice] = useState<null | {
    type: "success" | "error" | "notify";
    text: string;
  }>(null);

  // ✅ token em tempo real (mesma lógica do Registrar)
  const [tokenNow, setTokenNow] = useState<string | null>(() =>
    getAccessToken()
  );
  const hasAuthToken = useMemo(() => !!tokenNow, [tokenNow]);
  const evaUid = useMemo(() => getEvaAuthUid(), []);

  useEffect(() => {
    const onStorage = () => setTokenNow(getAccessToken());
    window.addEventListener("storage", onStorage);

    const t = setInterval(() => {
      const tkn = getAccessToken();
      setTokenNow((prev) => (prev !== tkn ? tkn : prev));
    }, 1500);

    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(t);
    };
  }, []);

  // abort controller persistente
  const abortRef = useRef<AbortController | null>(null);

  const fillFromColab = (c: ApiColaborador) => {
    setNomeCompleto(c.nome_completo ?? "");
    setCpfView(maskCPF(c.cpf ?? ""));
    setDataNasc(fromISOToBR(c.data_de_nascimento));
    setSetor(c.setor ?? "");
    setCargo(c.cargo ?? "");
    setSalario(salarioApiToView(c.salario));
    setEmail(c.email ?? "");
    setTelefone(c.telefone ?? "");
    setContatoEmerg(c.contato_de_emergencia ?? "");
    setValidadeContrato(fromISOToBR(c.validade_do_contrato));
    setStatusAtual((c.status_atual ?? "").toUpperCase());
  };

  useEffect(() => {
    if (colabFromState) {
      fillFromColab(colabFromState);
      setLoading(false);
    }
  }, [colabFromState]);

  // (mantive seu placeholder de fetch por id)
  useEffect(() => {
    if (!id) return;
    let disposed = false;

    (async () => {
      try {
        setLoading(true);
      } catch (e: any) {
        if (!disposed)
          setNotice({
            type: "error",
            text:
              e?.message ||
              "Falha ao carregar dados do colaborador para edição.",
          });
      } finally {
        if (!disposed) setLoading(false);
      }
    })();

    return () => {
      disposed = true;
    };
  }, [id]);

  async function handleSave() {
    setNotice(null);

    if (!id) {
      setNotice({ type: "error", text: "ID do colaborador ausente na rota." });
      return;
    }

    if (!tokenNow) {
      setNotice({
        type: "error",
        text: "Token não encontrado no localStorage (access_token). Faça login novamente.",
      });
      return;
    }

    const cpfDigits = onlyDigits(cpfView);
    const erros: string[] = [];
    if (!nomeCompleto.trim()) erros.push("Informe o Nome Completo.");
    if (!cpfDigits || cpfDigits.length !== 11)
      erros.push("CPF inválido (11 dígitos).");
    if (email.trim() && !isValidEmail(email.trim()))
      erros.push("E-mail inválido.");

    if (statusAtual && !STATUS_OPTIONS.some((o) => o.value === statusAtual)) {
      erros.push("Status inválido.");
    }

    if (erros.length) {
      setNotice({ type: "error", text: erros.join(" ") });
      return;
    }

    const isoNasc = toISODate(dataNasc);
    const isoValidade = toISODate(validadeContrato);
    const salarioNorm = salario
      ? salario.replace(/\./g, "").replace(",", ".")
      : "";

    const payload: Record<string, any> = {
      id,
      nome_completo: nomeCompleto.trim(),
      cpf: cpfDigits,

      ...(statusAtual ? { status: statusAtual } : {}),

      // aliases
      nome: nomeCompleto.trim(),
      CPF: cpfDigits,
      documento: cpfDigits,

      ...(isoNasc ? { data_de_nascimento: isoNasc } : {}),
      ...(setor.trim() ? { setor: setor.trim() } : { setor: null }),
      ...(cargo.trim() ? { cargo: cargo.trim() } : { cargo: null }),
      ...(salarioNorm ? { salario: salarioNorm } : { salario: null }),
      ...(email.trim() ? { email: email.trim() } : { email: null }),
      ...(telefone.trim() ? { telefone: telefone.trim() } : { telefone: null }),
      ...(contatoEmerg.trim()
        ? { contato_de_emergencia: contatoEmerg.trim() }
        : { contato_de_emergencia: null }),
      ...(isoValidade
        ? { validade_do_contrato: isoValidade }
        : { validade_do_contrato: null }),

      // compat/debug (se existir)
      ...(evaUid != null ? { usuario_id: evaUid } : {}),
    };

    const idempotencyKey = `rh-edit-${id}`;

    // aborta request anterior
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSubmitting(true);
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const url = `${API_POST}?debug=1`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Accept: "application/json",
          "x-debug": "1",
          "x-idempotency-key": idempotencyKey,

          // ✅ AQUI está a correção principal
          Authorization: `Bearer ${tokenNow}`,

          // (opcional) mantém headers de UID para debug/compat
          ...buildUidHeaders(),
        },

        // Se você usa cookies também, pode manter (não atrapalha):
        // credentials: "include",

        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeout);

      let data: any = null;
      try {
        data = await res.json();
      } catch {
        const text = await res.text();
        data = { message: text || null };
      }

      if (res.ok && data?.ok) {
        toast.success("Colaborador atualizado com sucesso!", {
          duration: 3000,
        });
        setNotice(null);
        navigate(
          `/recursos-humanos/colaborador/${encodeURIComponent(String(id))}`
        );
        return;
      }

      const msg =
        data?.erro ||
        (Array.isArray(data?.erros) && data.erros.join(" | ")) ||
        data?.detalhe ||
        data?.message ||
        `Falha (${res.status}).`;

      if (res.status === 401) {
        setNotice({ type: "error", text: `Sem autenticação (401). ${msg}` });
      } else if (res.status === 403) {
        setNotice({ type: "error", text: `Sem permissão (403). ${msg}` });
      } else if (res.status === 400) {
        setNotice({ type: "error", text: `Requisição inválida: ${msg}` });
      } else if (res.status === 409) {
        setNotice({ type: "error", text: `Conflito ao atualizar: ${msg}` });
      } else if (res.status >= 500) {
        setNotice({ type: "error", text: `Erro do servidor: ${msg}` });
      } else {
        setNotice({ type: "error", text: msg });
      }
    } catch (err: any) {
      clearTimeout(timeout);
      const msg =
        err?.name === "AbortError"
          ? "Tempo de resposta excedido (ou requisição cancelada)."
          : err?.message || "Erro de rede.";
      setNotice({ type: "error", text: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ width: "100%" }}>
      <Toaster position="bottom-center" reverseOrder={false} />

      <TitleButtons
        title={`Recursos Humanos — Editar Colaborador ${id ? `#${id}` : ""}`}
        buttons={[
          {
            label: "Cancelar",
            onClick: () => navigate(-1),
            variant: "secondary",
          },
          {
            label: submitting ? "Salvando..." : "Salvar alterações",
            onClick: handleSave,
            variant: "primary",
          },
        ]}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        style={{
          display: "grid",
          gap: 10,
          width: "100%",
          opacity: loading ? 0.6 : 1,
        }}
      >
        {/* INFO TOKEN */}
        {!hasAuthToken ? (
          <AnimatePresence>
            <NoticeBanner
              notice={{
                type: "error",
                text: "Nenhum token encontrado no localStorage (access_token). Faça login novamente.",
              }}
            />
          </AnimatePresence>
        ) : (
          <AnimatePresence>
            <NoticeBanner
              notice={{
                type: "success",
                text: "Token encontrado. A requisição será enviada com Authorization: Bearer <token>.",
              }}
            />
          </AnimatePresence>
        )}

        {/* ===== Obrigatórios ===== */}
        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <TextField
            label="Nome Completo *"
            value={nomeCompleto}
            onChange={setNomeCompleto}
            placeholder="Ex.: Fernanda Lima"
            style={{ width: "100%" }}
            disabled={loading}
          />
        </motion.div>

        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <TextField
            label="CPF *"
            value={cpfView}
            onChange={(v) => setCpfView(maskCPF(v))}
            placeholder="000.000.000-00"
            style={{ width: "100%" }}
            disabled={loading}
          />
        </motion.div>

        {/* ===== STATUS ===== */}
        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <SelectField
            label="Status Atual"
            value={statusAtual || ""}
            onChange={(v) => setStatusAtual(v)}
            options={STATUS_OPTIONS}
            disabled={loading}
            placeholder="Selecione o status..."
          />
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
            Ao salvar, este status será atualizado e registrado no histórico.
          </div>
        </motion.div>

        {/* ===== Opcionais ===== */}
        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <TextField
            label="Data de Nascimento"
            value={dataNasc}
            onChange={setDataNasc}
            date
            style={{ width: "100%" }}
            disabled={loading}
          />
        </motion.div>

        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <TextField
            label="Departamento (Setor)"
            value={setor}
            onChange={setSetor}
            placeholder="Ex.: Logística"
            style={{ width: "100%" }}
            disabled={loading}
          />
        </motion.div>

        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <TextField
            label="Cargo"
            value={cargo}
            onChange={setCargo}
            placeholder="Ex.: Coordenadora"
            style={{ width: "100%" }}
            disabled={loading}
          />
        </motion.div>

        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <TextField
            label="Salário"
            value={salario}
            onChange={setSalario}
            placeholder="Ex.: 7.830,90"
            style={{ width: "100%" }}
            disabled={loading}
          />
        </motion.div>

        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <TextField
            label="E-mail"
            value={email}
            onChange={setEmail}
            placeholder="nome.sobrenome@empresa.com"
            style={{ width: "100%" }}
            disabled={loading}
          />
        </motion.div>

        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <TextField
            label="Telefone"
            value={telefone}
            onChange={setTelefone}
            placeholder="(51) 94444-8888"
            style={{ width: "100%" }}
            disabled={loading}
          />
        </motion.div>

        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <TextField
            label="Contato de Emergência"
            value={contatoEmerg}
            onChange={setContatoEmerg}
            placeholder="Nome - Telefone"
            style={{ width: "100%" }}
            disabled={loading}
          />
        </motion.div>

        <motion.div variants={itemVariants} style={{ width: "100%" }}>
          <TextField
            label="Validade do Contrato"
            value={validadeContrato}
            onChange={setValidadeContrato}
            date
            style={{ width: "100%" }}
            disabled={loading}
          />
        </motion.div>

        <AnimatePresence>
          {notice && (
            <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
          )}
        </AnimatePresence>

        <motion.div
          variants={itemVariants}
          style={{ display: "flex", gap: 8, marginTop: 4 }}
        >
          <Button
            text={submitting ? "Salvando..." : "Salvar alterações"}
            onClick={handleSave}
            disabled={submitting || loading || !hasAuthToken}
          />
        </motion.div>
      </motion.div>

      <div style={{ height: 24 }} />
    </div>
  );
};

export default EditarColaborador;
